import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useEffect, useMemo, useState } from "react";
import { LoaderCircle, Plug, Search } from "lucide-react";
import type { ConnectorManifest } from "@io-spoke/contracts/integrations";
import { Card, CardContent } from "@components/ui/card";
import { Input } from "@components/ui/input";
import { listConnectorCatalog, requestConnector } from "../../lib/integrations";
import { toast } from "sonner";
import { IntegrationCatalogCard } from "./IntegrationCatalogCard";

type Props = { onConfigure: (connector: ConnectorManifest) => void };

export function IntegrationCatalog({ onConfigure }: Props) {
  const [connectors, setConnectors] = useState<ConnectorManifest[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requested, setRequested] = useState<Set<string>>(() => new Set());
  const requestAction = useAsyncAction();
  const [requesting, setRequesting] = useState<string | null>(null);

  useEffect(() => {
    void listConnectorCatalog().then(setConnectors).catch((cause) => {
      setError(cause instanceof Error ? cause.message : "Unable to load integration catalog.");
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return connectors;
    return connectors.filter((connector) => `${connector.name} ${connector.description} ${connector.category}`.toLowerCase().includes(normalized));
  }, [connectors, query]);
  const customerVisible = filtered.filter((connector) => connector.availability !== "demo");
  const available = customerVisible.filter((connector) => connector.availability === "available");
  const planned = customerVisible.filter((connector) => connector.availability === "planned");

  const request = (connector: ConnectorManifest) => requestAction.run(async () => {
    setRequesting(connector.id);
    try {
      await requestConnector(connector.id);
      setRequested((current) => new Set(current).add(connector.id));
      toast.success(`${connector.name} request recorded.`);
    } catch (cause) { toast.error(cause instanceof Error ? cause.message : "Unable to record integration request."); }
    finally { setRequesting(null); }
  });
  const connectorCard = (connector: ConnectorManifest) => (
    <IntegrationCatalogCard key={connector.id} connector={connector} onConfigure={onConfigure}
      onRequest={(item) => void request(item)} requested={requested.has(connector.id)}
      requestsBusy={requestAction.pending} requestPending={requestAction.pending && requesting === connector.id} />
  );

  return <section className="space-y-4" aria-labelledby="integration-catalog-title">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div><h2 className="text-lg font-semibold" id="integration-catalog-title">Integration catalog</h2><p className="mt-1 text-sm text-muted-foreground">One capability model across every system your teams already use.</p></div>
      <div className="relative w-full sm:w-72"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground"/><Input aria-label="Search integrations" className="pl-9" placeholder="Search integrations" value={query} onChange={(event) => setQuery(event.target.value)}/></div>
    </div>
    {loading ? <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><LoaderCircle className="h-4 w-4 animate-spin"/>Loading integration catalog…</div> : null}
    {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
    {!loading && !error && customerVisible.length === 0 ? <Card><CardContent className="py-10 text-center"><Plug className="mx-auto mb-3 h-7 w-7 text-muted-foreground"/><p className="font-medium">No integrations match “{query}”</p><p className="mt-1 text-sm text-muted-foreground">Try a provider name or category.</p></CardContent></Card> : null}
    {available.length ? <div className="space-y-3"><div><h3 className="text-sm font-semibold">Available now</h3><p className="text-xs text-muted-foreground">Working connection paths you can use or explore today.</p></div><div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{available.map(connectorCard)}</div></div> : null}
    {planned.length ? <div className="space-y-3 pt-4"><div><h3 className="text-sm font-semibold">Coming next</h3><p className="text-xs text-muted-foreground">Planned adapters—not yet available to connect.</p></div><div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{planned.map(connectorCard)}</div></div> : null}
  </section>;
}
